/**
 * Platform Decorator Base Class
 *
 * Base class for zone-specific platform decorators.
 * Provides common decoration logic and animation system.
 *
 * CRITICAL: All decorations are purely visual - they do not affect physics!
 */

import Phaser from 'phaser';
import { SurfaceType } from '@/config/constants';
import { SeededRandom } from '@/utils/SeededRandom';
import { isCollectibleDecoration } from '../../config/collectibleMapping';

export interface DecorationConfig {
  type: string;
  chance: number;
  allowedSurfaces?: SurfaceType[];
  minWidth?: number; // Only add to platforms wider than this
  placement?: 'top' | 'underside'; // Where to place the decoration
}

export interface AnimatedDecoration {
  graphics?: Phaser.GameObjects.Graphics; // PHASE 7C: Optional for backward compatibility
  sprite?: Phaser.GameObjects.Sprite; // PHASE 7C: New sprite-based decorations
  type: string;
  animationPhase: number;
  animationSpeed: number;
}

export interface CollectibleDecorationPosition {
  type: string;
  x: number;
  y: number;
  decorationIndex: number;
}

export abstract class PlatformDecoratorBase {
  protected scene: Phaser.Scene;
  protected surfaceType: SurfaceType;
  protected random: SeededRandom;
  protected animatedElements: AnimatedDecoration[] = [];
  protected collectiblePositions: CollectibleDecorationPosition[] = [];
  protected decorationCounter: number = 0;

  // PHASE 6: Animation frame throttling for better performance
  // Update animations every N frames instead of every frame
  // At 60 FPS, updating every 2 frames (30 updates/sec) is imperceptible
  private animationFrameCounter: number = 0;
  private readonly ANIMATION_UPDATE_INTERVAL: number = 2; // Update every 2 frames

  constructor(scene: Phaser.Scene, surfaceType: SurfaceType, random: SeededRandom) {
    this.scene = scene;
    this.surfaceType = surfaceType;
    this.random = random;
  }

  /**
   * Get zone-specific decoration configurations
   * Must be implemented by each zone decorator
   */
  protected abstract getDecorations(): DecorationConfig[];

  /**
   * Decorate a platform with zone-specific decorations
   */
  public decorate(
    platformContainer: Phaser.GameObjects.Container,
    _x: number,
    _y: number,
    width: number,
    height: number
  ): void {
    const decorations = this.getDecorations();

    for (const decoration of decorations) {
      // Check if decoration should spawn (probability)
      if (this.random.next() > decoration.chance) {
        continue;
      }

      // Check surface type constraint
      if (decoration.allowedSurfaces && !decoration.allowedSurfaces.includes(this.surfaceType)) {
        continue;
      }

      // Check minimum width constraint
      if (decoration.minWidth && width < decoration.minWidth) {
        continue;
      }

      const placement = decoration.placement || 'top';

      // Check if this is a collectible decoration
      const isCollectible = placement === 'top' && isCollectibleDecoration(decoration.type);

      if (isCollectible) {
        // For collectible decorations, only track the position
        // DO NOT draw them - they will be spawned as collectible entities instead
        this.trackCollectiblePosition(decoration.type, width, height);
      } else {
        // For non-collectible decorations, draw them normally
        this.addDecoration(platformContainer, decoration.type, placement, width, height);
      }
    }
  }

  /**
   * Add a specific decoration to the platform
   * Subclasses override this to implement zone-specific drawing methods
   */
  protected abstract addDecoration(
    platformContainer: Phaser.GameObjects.Container,
    decorationType: string,
    placement: 'top' | 'underside',
    width: number,
    height: number
  ): void;

  /**
   * Update animated decorations
   * PHASE 6: Frame-throttled updates for better performance
   * Call this from the platform's update method or scene update
   */
  public updateAnimations(_deltaTime: number): void {
    // PHASE 6: Only update animations every N frames (massive CPU savings)
    // At 60 FPS, updating every 2 frames = 30 animation updates/sec (imperceptible)
    this.animationFrameCounter++;
    if (this.animationFrameCounter < this.ANIMATION_UPDATE_INTERVAL) {
      return; // Skip this frame
    }
    this.animationFrameCounter = 0;

    // Update all animated elements (only runs every 2 frames now)
    for (const element of this.animatedElements) {
      // Increment phase by 2x to maintain same animation speed
      element.animationPhase += element.animationSpeed * this.ANIMATION_UPDATE_INTERVAL;

      // PHASE 7C: Support both Graphics and Sprite objects
      const gameObject = element.sprite || element.graphics;
      if (!gameObject) continue;

      if (element.type === 'firefly-orbit') {
        // Make firefly orbit/bob
        const verticalOffset = Math.sin(element.animationPhase) * 8;
        const horizontalOffset = Math.cos(element.animationPhase * 0.7) * 5;
        gameObject.x = horizontalOffset;
        gameObject.y = verticalOffset;

        // Pulse the glow (alpha pulse)
        const alphaPulse = 0.8 + Math.sin(element.animationPhase * 2) * 0.2;
        gameObject.setAlpha(alphaPulse);
      } else if (element.type === 'frog-idle') {
        // Make frog breathe
        const scale = 1 + Math.sin(element.animationPhase) * 0.12;
        gameObject.setScale(scale);
      } else if (element.type === 'lava-pulse') {
        // Make lava pulse with glow
        const alphaPulse = 0.7 + Math.sin(element.animationPhase) * 0.3;
        gameObject.setAlpha(alphaPulse);
        const scalePulse = 1 + Math.sin(element.animationPhase * 1.5) * 0.1;
        gameObject.setScale(scalePulse);
      } else if (element.type === 'smoke-rise') {
        // Make smoke rise slowly
        const riseOffset = Math.sin(element.animationPhase) * 5;
        gameObject.y = -riseOffset;
        const fadeOut = 1 - Math.abs(Math.sin(element.animationPhase)) * 0.3;
        gameObject.setAlpha(fadeOut * 0.3);
      } else if (element.type === 'butterfly-flutter') {
        // Make butterfly flutter wings (gentle bobbing)
        const bobOffset = Math.sin(element.animationPhase) * 3;
        gameObject.y = bobOffset;
        // Slight scale variation for wing flutter
        const scaleVariation = 1 + Math.sin(element.animationPhase * 2) * 0.05;
        gameObject.setScale(scaleVariation, 1);
      } else if (element.type === 'moth-hover') {
        // PHASE 7C: Moth hovering animation
        const hoverOffset = Math.sin(element.animationPhase) * 2;
        gameObject.y = hoverOffset;
      } else if (element.type === 'feather-float') {
        // PHASE 7C: Feather floating animation
        const floatOffset = Math.sin(element.animationPhase) * 4;
        gameObject.y = floatOffset;
        const sway = Math.sin(element.animationPhase * 0.5) * 2;
        gameObject.x = sway;
      } else if (element.type === 'star-twinkle') {
        // PHASE 7C: Star twinkling animation
        const alphaPulse = 0.6 + Math.sin(element.animationPhase) * 0.4;
        gameObject.setAlpha(alphaPulse);
        const scalePulse = 0.9 + Math.sin(element.animationPhase * 1.2) * 0.1;
        gameObject.setScale(scalePulse);
      } else if (element.type === 'sparkle-pulse') {
        // PHASE 7C: Sparkle pulsing animation
        const alphaPulse = 0.7 + Math.sin(element.animationPhase) * 0.3;
        gameObject.setAlpha(alphaPulse);
        const scalePulse = 1 + Math.sin(element.animationPhase * 1.5) * 0.15;
        gameObject.setScale(scalePulse);
      } else if (element.type === 'energy-pulse') {
        // PHASE 7C: Energy orb pulsing animation
        const scalePulse = 1 + Math.sin(element.animationPhase) * 0.12;
        gameObject.setScale(scalePulse);
        const alphaPulse = 0.8 + Math.sin(element.animationPhase * 1.3) * 0.2;
        gameObject.setAlpha(alphaPulse);
      } else if (element.type === 'energy-wave') {
        // PHASE 7C: Energy tendril waving animation
        const waveX = Math.sin(element.animationPhase) * 3;
        gameObject.x = waveX;
        const alphaPulse = 0.7 + Math.sin(element.animationPhase * 0.8) * 0.3;
        gameObject.setAlpha(alphaPulse);
      }
    }
  }

  /**
   * Get animated elements for external update
   */
  public getAnimatedElements(): AnimatedDecoration[] {
    return this.animatedElements;
  }

  /**
   * Get collectible decoration positions
   * Used to spawn collectible entities
   */
  public getCollectiblePositions(): CollectibleDecorationPosition[] {
    return this.collectiblePositions;
  }

  /**
   * Track a collectible decoration position
   * Calculates and stores the actual local position using current random state
   */
  protected trackCollectiblePosition(
    type: string,
    platformWidth: number,
    platformHeight: number
  ): void {
    // CRITICAL FIX: Match decoration positioning (use -platformHeight/2 only)
    // Constant offsets are baked into textures, so collectibles should align with platform edge
    // Most top decorations use: x = random.nextFloat(-width * 0.4, width * 0.4)
    const localX = this.random.nextFloat(-platformWidth * 0.4, platformWidth * 0.4);
    const localY = -platformHeight / 2; // Platform top edge (texture has offset)

    this.collectiblePositions.push({
      type,
      x: localX, // Store actual local X position
      y: localY, // Store actual local Y position
      decorationIndex: this.decorationCounter++,
    });
  }

  /**
   * Cleanup
   * PHASE 7C: Destroy both Graphics and Sprite objects
   */
  public destroy(): void {
    for (const element of this.animatedElements) {
      if (element.graphics) {
        element.graphics.destroy();
      }
      if (element.sprite) {
        element.sprite.destroy();
      }
    }
    this.animatedElements = [];
    this.collectiblePositions = [];
  }
}
